import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, Phone, X } from "lucide-react";
import { useLang } from "@/lib/i18n";

export default function Navbar({ onNavigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  const go = (target) => {
    setOpen(false);
    onNavigate(target);
  };

  const LINKS = [
    { label: t("nav_services"), target: "services" },
    { label: t("nav_quote"), target: "quote" },
    { label: t("nav_book"), target: "book" },
    { label: t("nav_areas"), target: "areas" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled || open
          ? "bg-[#0E1A2B]/90 backdrop-blur-md border-b border-white/10 shadow-2xl"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto flex items-center justify-between h-20">
        <button
          onClick={() => onNavigate("top")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo-btn"
        >
          <img
            src="/logo.png"
            alt="PERETE Flooring and Carpet Installation Inc. logo"
            className="h-12 w-12 rounded-full ring-2 ring-[#F26522]/70 object-cover transition-transform duration-300 group-hover:scale-105"
            data-testid="nav-logo-img"
          />
          <div className="text-left leading-tight">
            <span className="block font-display font-extrabold text-white tracking-tight text-lg">
              PERETE
            </span>
            <span className="hidden min-[420px]:block font-mono text-[10px] uppercase tracking-[0.2em] text-[#F26522]">
              {t("brand_sub")}
            </span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.target}
              onClick={() => onNavigate(l.target)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-[#F26522] transition-colors duration-200"
              data-testid={`nav-link-${l.target}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex rounded-full border border-white/20 overflow-hidden">
            {["en", "es"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.15em] transition-colors duration-200 ${
                  lang === l
                    ? "bg-[#F26522] text-white"
                    : "text-slate-300 hover:text-white"
                }`}
                data-testid={`lang-toggle-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href="tel:3608617655"
            className="hidden sm:flex items-center gap-2 text-white font-semibold text-sm hover:text-[#F26522] transition-colors duration-200"
            data-testid="phone-link"
          >
            <Phone className="h-4 w-4" />
            360-861-7655
          </a>
          <button
            onClick={() => onNavigate("quote")}
            className="hidden sm:block rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold text-sm px-5 py-2.5 transition-[background-color,transform] duration-200 hover:scale-[1.03] active:scale-95"
            data-testid="nav-quote-btn"
          >
            {t("nav_cta")}
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="lg:hidden flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white hover:border-[#F26522] transition-colors duration-200"
            data-testid="nav-menu-btn"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-t border-white/10 bg-[#0E1A2B]/95 backdrop-blur-md px-4 sm:px-8 pt-4 pb-6"
            data-testid="nav-mobile-menu"
          >
            <ul className="divide-y divide-white/10">
              {LINKS.map((l) => (
                <li key={l.target}>
                  <button
                    onClick={() => go(l.target)}
                    className="w-full flex items-center justify-between py-4 font-display font-bold text-lg text-white hover:text-[#F26522] transition-colors duration-200"
                    data-testid={`nav-mobile-link-${l.target}`}
                  >
                    {l.label}
                    <ArrowUpRight className="h-5 w-5 text-[#F26522]" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <a
                href="tel:3608617655"
                className="flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-semibold text-sm py-3.5 hover:border-[#F26522] transition-colors duration-200"
                data-testid="nav-mobile-phone-link"
              >
                <Phone className="h-4 w-4" /> 360-861-7655
              </a>
              <button
                onClick={() => go("quote")}
                className="rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold text-sm py-3.5 transition-colors duration-200"
                data-testid="nav-mobile-quote-btn"
              >
                {t("nav_cta")}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
